import { useState } from 'react';
import { Card, Avatar, Badge, Icon } from '@lib/ui';
import { PersonaPreview, PersonaTestPanel } from '../../components';
import type { WizardPersona } from '../../types';
import { DECISION_DIMENSIONS, defaultDecisions } from '../../types';

export type TestReviewStepProps = {
  persona: Partial<WizardPersona>;
  onChange: (updates: Partial<WizardPersona>) => void;
};

export function TestReviewStep({ persona }: TestReviewStepProps) {
  const [selectedChannel, setSelectedChannel] = useState('LinkedIn');
  const [feedback, setFeedback] = useState<'yes' | 'no' | 'refine' | null>(null);

  const decisions = persona.decisions || defaultDecisions;
  const traits = persona.voice?.traits || [];

  // Get human-readable decision labels
  const getDecisionLabel = (key: keyof typeof decisions) => {
    const dimension = DECISION_DIMENSIONS.find((d) => d.key === key);
    const level = dimension?.levels.find((l) => l.value === decisions[key]);
    return level?.label || decisions[key];
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Test & Review</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-dark-400">
          Generate sample content to test your persona
        </p>
      </div>

      {/* Persona summary card */}
      <Card skin="bordered" className="p-4">
        <div className="flex items-start gap-4">
          <Avatar
            size="lg"
            className="bg-primary-500 text-white font-semibold"
            name={persona.avatar || persona.name?.slice(0, 2).toUpperCase() || '??'}
          />

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {persona.name || 'Unnamed Persona'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-dark-400 mt-1">
              {persona.tagline || 'No tagline set'}
            </p>

            {/* Traits */}
            {traits.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {traits.map((trait) => (
                  <Badge key={trait} variant="soft" color="primary" className="text-xs">
                    {trait}
                  </Badge>
                ))}
              </div>
            )}

            {/* Key decisions summary */}
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-dark-400">
              <span>{getDecisionLabel('confidence')}</span>
              <span>•</span>
              <span>{getDecisionLabel('formality')}</span>
              <span>•</span>
              <span>{getDecisionLabel('callToAction')}</span>
            </div>
          </div>

          {/* Completeness indicator */}
          <div className="text-right">
            <div className="flex items-center gap-1 text-success-600 dark:text-success-400">
              <Icon name="check" size="md" />
              <span className="text-sm font-medium">Ready</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Side-by-side: Preview and Test Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Live preview */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-dark-200 mb-3">
            Live Preview
          </h3>
          <PersonaPreview
            persona={persona}
            selectedChannel={selectedChannel}
            onChannelChange={setSelectedChannel}
          />
        </div>

        {/* Test panel */}
        <div>
          <PersonaTestPanel
            persona={persona}
            onFeedback={(fb) => {
              setFeedback(fb);
            }}
          />
        </div>
      </div>

      {/* Feedback message */}
      {feedback && (
        <Card
          skin="bordered"
          className={`p-4 ${
            feedback === 'yes'
              ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800'
              : feedback === 'no'
                ? 'bg-error-50 dark:bg-error-900/20 border-error-200 dark:border-error-800'
                : 'bg-warning-50 dark:bg-warning-900/20 border-warning-200 dark:border-warning-800'
          }`}
        >
          {feedback === 'yes' && (
            <p className="text-sm text-success-700 dark:text-success-300">
              Great! Your persona is generating content that matches your expectations. You can now
              save and start using it.
            </p>
          )}
          {feedback === 'no' && (
            <p className="text-sm text-error-700 dark:text-error-300">
              No worries! Go back to the Decisions step to adjust the framing, confidence, or other
              settings. Small changes can make a big difference.
            </p>
          )}
          {feedback === 'refine' && (
            <p className="text-sm text-warning-700 dark:text-warning-300">
              Good feedback! Consider adjusting the decision settings or adding more example phrases
              to better calibrate the output.
            </p>
          )}
        </Card>
      )}

      {/* Final actions hint */}
      <div className="text-center text-sm text-gray-500 dark:text-dark-400">
        When you're satisfied with the results, click "Complete" to finish creating your persona.
      </div>
    </div>
  );
}
